import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { formatAmount, parseFloatFromString } from '../../utils/formatters';

const MIN_CREATE_NAME_LENGTH = 2;

const AllocationLocationSelector = ({
  locations = [],
  selectedLocationId = '',
  onSelect,
  onCreate,
  onRename,
  onDelete,
  autoFocus = false,
  hideResults = false,
  tokenTicker = '',
  tokenLocationBalances = [],
  inputLabel = 'Target location',
  currentSelectionLabel = 'Current target',
  emptySelectionLabel = 'No target location selected',
  placeholder = 'Type to filter or create a location...',
}) => {
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [deleteCandidateId, setDeleteCandidateId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const inputId = useId();
  const inputRef = useRef(null);

  useEffect(() => {
    if (!autoFocus) return;
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [autoFocus]);

  const tokenBalanceByLocationId = useMemo(() => {
    const balances = new Map();
    (tokenLocationBalances || []).forEach(balance => {
      if (!balance?.locationId) return;
      const quantity = parseFloatFromString(balance.quantity || '0');
      if (quantity <= 0) return;
      balances.set(balance.locationId, {
        ...balance,
        quantity,
        quantityLabel: formatAmount(balance.quantity || '0'),
      });
    });
    return balances;
  }, [tokenLocationBalances]);

  const sortedLocations = useMemo(() => (
    [...(locations || [])].sort((a, b) => {
      const aBalance = tokenBalanceByLocationId.get(a.id)?.quantity || 0;
      const bBalance = tokenBalanceByLocationId.get(b.id)?.quantity || 0;
      if (aBalance > 0 || bBalance > 0) {
        if (aBalance !== bBalance) return bBalance - aBalance;
      }
      return (a.name || '').localeCompare(b.name || '');
    })
  ), [locations, tokenBalanceByLocationId]);

  const normalizedFilter = filter.trim().toLowerCase();
  const filteredLocations = useMemo(() => {
    if (!normalizedFilter) return sortedLocations;
    return sortedLocations.filter(location => (location.name || '').toLowerCase().includes(normalizedFilter));
  }, [sortedLocations, normalizedFilter]);

  const selectedLocation = sortedLocations.find(location => location.id === selectedLocationId) || null;
  const hasExactMatch = normalizedFilter.length > 0 && sortedLocations.some(
    location => (location.name || '').toLowerCase() === normalizedFilter
  );
  const canCreate = filter.trim().length >= MIN_CREATE_NAME_LENGTH && !hasExactMatch;

  const createLocation = async () => {
    const name = filter.trim();
    if (!canCreate || !onCreate) return;
    setCreating(true);
    try {
      const created = await onCreate(name);
      if (created?.id) {
        onSelect?.(created.id, created);
        setFilter('');
      }
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (location) => {
    setEditingId(location.id);
    setEditingName(location.name || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const submitEdit = async (locationId) => {
    const name = editingName.trim();
    if (!name || !onRename) return;
    setRenaming(true);
    try {
      const renamed = await onRename(locationId, name);
      if (renamed !== false) {
        setEditingId(null);
        setEditingName('');
      }
    } finally {
      setRenaming(false);
    }
  };

  const requestDelete = (location) => {
    setEditingId(null);
    setEditingName('');
    setDeleteCandidateId(location.id);
  };

  const cancelDelete = () => setDeleteCandidateId(null);

  const submitDelete = async (location) => {
    if (!onDelete || !location?.id) return;
    setDeleting(true);
    try {
      const deleted = await onDelete(location);
      if (deleted !== false) {
        if (location.id === selectedLocationId) {
          onSelect?.('', null);
        }
        setDeleteCandidateId(null);
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{inputLabel}</label>
        <div className="flex items-center gap-2">
          <input
            id={inputId}
            ref={inputRef}
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canCreate) {
                e.preventDefault();
                createLocation();
              }
            }}
            placeholder={placeholder}
            className="flex-1 p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          {canCreate && (
            <button
              type="button"
              onClick={createLocation}
              disabled={creating}
              className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
          )}
        </div>
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {selectedLocation ? (
            <>
              {currentSelectionLabel}: {selectedLocation.name}
              <button
                type="button"
                onClick={() => onSelect?.('', null)}
                className="ml-2 underline text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                clear
              </button>
            </>
          ) : (
            emptySelectionLabel
          )}
        </div>
      </div>

      {!hideResults && (
        <div className="divide-y divide-gray-200 dark:divide-gray-700 rounded-md border border-gray-200 dark:border-gray-700 max-h-72 overflow-y-auto">
          {filteredLocations.map(location => {
            const isSelected = location.id === selectedLocationId;
            const isEditing = editingId === location.id;
            const isConfirmingDelete = deleteCandidateId === location.id;
            const tokenBalance = tokenBalanceByLocationId.get(location.id);

            return (
              <div
                key={location.id}
                className={`flex items-stretch ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-800'}`}
              >
                {isConfirmingDelete ? (
                  <div className="flex-1 px-3 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm text-gray-700 dark:text-gray-300">Delete "{location.name}"? Only allowed when no transactions use it.</div>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={cancelDelete} className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Cancel</button>
                        <button
                          type="button"
                          onClick={() => submitDelete(location)}
                          disabled={deleting}
                          className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deleting ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : isEditing ? (
                  <div className="flex-1 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            submitEdit(location.id);
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelEdit();
                          }
                        }}
                        autoFocus
                        disabled={renaming}
                        className="flex-1 p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                      <button type="button" onClick={cancelEdit} className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Cancel</button>
                      <button
                        type="button"
                        onClick={() => submitEdit(location.id)}
                        disabled={renaming || !editingName.trim()}
                        className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {renaming ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => onSelect?.(location.id, location)}
                      className={`flex-1 text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-800 dark:text-gray-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate">{location.name}</span>
                        {tokenBalance && (
                          <span className="ml-3 shrink-0 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
                            {tokenBalance.quantityLabel} {tokenTicker}
                          </span>
                        )}
                        {isSelected && (
                          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                        )}
                      </div>
                    </button>
                    <div className="flex items-center px-2">
                      <button
                        type="button"
                        onClick={() => startEdit(location)}
                        className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                        title="Rename location"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                      </button>
                      {onDelete && (
                        <button
                          type="button"
                          onClick={() => requestDelete(location)}
                          className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          title="Delete location"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {filteredLocations.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-600 dark:text-gray-300">
              {filter.trim().length >= MIN_CREATE_NAME_LENGTH ? 'No locations match. Create it above.' : `Type at least ${MIN_CREATE_NAME_LENGTH} characters to create a new location.`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AllocationLocationSelector;
